// One tenant's view of every model call site: which layer serves it (its own
// override, the platform's, or the code default) and what actually goes on
// the wire. Rows open the tenant editor; the platform view stays at /prompts.
import { SlidersHorizontal } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import type { PromptScope } from "@gb/admin-contracts";
import { DataTable } from "@/components/gb/data-table";
import { EmptyState } from "@/components/gb/empty-state";
import { PageHeader } from "@/components/gb/page-header";
import { cn } from "@/lib/utils";
import { useTenant } from "../tenants/tenants-api";
import { useTenantTouchpoints } from "./tenant-prompts-api";

const SCOPE_LABELS: Record<PromptScope, string> = {
  tenant: "Tenant override",
  platform: "Inherits platform",
  code: "Code default",
};

export function ScopeBadge({ scope }: { scope: PromptScope }) {
  return (
    <span
      className={cn(
        "font-display text-[0.6rem] font-semibold tracking-[0.08em] uppercase",
        scope === "tenant" ? "text-brand" : scope === "platform" ? "text-muted" : "text-faint",
      )}
    >
      {SCOPE_LABELS[scope]}
    </span>
  );
}

/**
 * The touchpoint list without page chrome, so the tenant detail page can show
 * it as a tab and the standalone route can wrap it in a header — one table,
 * two doors, no drift.
 */
export function TenantPromptsList({ tenantId }: { tenantId: string }) {
  const touchpoints = useTenantTouchpoints(tenantId);
  const navigate = useNavigate();

  if (touchpoints.isError) {
    return (
      <EmptyState
        icon={SlidersHorizontal}
        title="Couldn’t load touchpoints"
        description={String(touchpoints.error)}
      />
    );
  }

  return (
    <div>
      <p className="max-w-[640px] text-[0.8rem] text-muted">
        A tenant override wins over the platform version, which wins over the
        code default. A touchpoint with no tenant version simply inherits —
        reverting one is a click on the touchpoint.
      </p>
      <div className="mt-4">
        <DataTable
          columns={[
            {
              key: "name",
              header: "Touchpoint",
              render: (t) => (
                <div className="min-w-0">
                  <div className="truncate text-[0.85rem] font-semibold text-ink">{t.name}</div>
                  <div className="truncate font-mono text-[0.7rem] text-faint">{t.slug}</div>
                </div>
              ),
            },
            {
              key: "scope",
              header: "Serving",
              width: 140,
              render: (t) => <ScopeBadge scope={t.effectiveScope} />,
            },
            {
              key: "model",
              header: "Effective model",
              width: 220,
              render: (t) => (
                <span className="truncate font-mono text-[0.72rem] text-ink">
                  {t.effectiveModel}
                </span>
              ),
            },
            {
              key: "tenantVersion",
              header: "Tenant version",
              width: 120,
              render: (t) => (
                <span className="font-mono text-[0.75rem] text-muted">
                  {t.tenantActiveVersion !== null ? `v${t.tenantActiveVersion}` : "—"}
                  {t.tenantOutOfDateVersion !== null && (
                    <span className="text-warn"> · v{t.tenantOutOfDateVersion} out of date</span>
                  )}
                </span>
              ),
            },
            {
              key: "platformVersion",
              header: "Platform version",
              width: 130,
              render: (t) => (
                <span className="font-mono text-[0.75rem] text-muted">
                  {t.platformActiveVersion !== null ? `v${t.platformActiveVersion}` : "v0 (code)"}
                  {t.platformOutOfDateVersion !== null && (
                    <span className="text-warn"> · v{t.platformOutOfDateVersion} out of date</span>
                  )}
                </span>
              ),
            },
          ]}
          rows={touchpoints.data ?? []}
          getRowKey={(t) => t.slug}
          onSelect={(t) => void navigate(`/tenants/${tenantId}/prompts/${t.slug}`)}
          emptyMessage="No touchpoints."
        />
      </div>
    </div>
  );
}

export function TenantPromptsPage() {
  const { tenantId = "" } = useParams();
  const tenant = useTenant(tenantId);

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link to={`/tenants/${tenantId}`} className="text-brand hover:underline">
          ← {tenant.data?.name ?? "Tenant"}
        </Link>
      </div>
      <PageHeader eyebrow={tenant.data?.name ?? "Tenant"} title="Prompts & models" />
      <div className="mt-1" />
      <TenantPromptsList tenantId={tenantId} />
    </div>
  );
}
